import { useEffect, useRef, useState } from 'react'
import { ArrowPathIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'

export default function LaborQueryButton({
  label,
  disabled,
  cachedTitle,
  showCacheMenu,
  onRunQuery,
  onClearCacheAndRun,
}: {
  label: string
  disabled: boolean
  cachedTitle?: string
  showCacheMenu: boolean
  onRunQuery: () => void
  onClearCacheAndRun: () => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const spinning = label === 'Running…'

  useEffect(() => {
    if (!menuOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return
      setMenuOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  useEffect(() => {
    if (disabled) setMenuOpen(false)
  }, [disabled])

  const labelContent = spinning ? (
    <>
      <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
      {label}
    </>
  ) : (
    label
  )

  if (!showCacheMenu) {
    return (
      <Button
        gradient
        disabled={disabled}
        title={cachedTitle}
        onClick={onRunQuery}
      >
        {labelContent}
      </Button>
    )
  }

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={[
          'flex h-9 shrink-0 items-stretch overflow-hidden rounded-lg text-sm font-medium text-white shadow-sm',
          '[background:var(--gradient-primary)]',
          disabled ? 'cursor-not-allowed opacity-50' : '',
        ].join(' ')}
      >
        <button
          type="button"
          onClick={onRunQuery}
          disabled={disabled}
          title={cachedTitle}
          className="inline-flex items-center gap-1.5 px-3 hover:bg-black/10 disabled:cursor-not-allowed"
        >
          {labelContent}
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            if (disabled) return
            setMenuOpen((open) => !open)
          }}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="More query actions"
          className="flex w-8 items-center justify-center border-l border-white/25 hover:bg-black/10 disabled:cursor-not-allowed"
        >
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      </div>
      {menuOpen ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 min-w-[14rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => {
              setMenuOpen(false)
              onClearCacheAndRun()
            }}
          >
            Clear Cache and Run Query
          </button>
        </div>
      ) : null}
    </div>
  )
}
